import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { setActivityHandler, setUnauthorizedHandler } from '/src/api/core/sessionEvents';
import { useGlobalStore, useUserStore } from '/src/context';
import {
  SESSION_EXPIRED_REASON,
  getSessionExpiration,
  getSessionToken,
  isSessionExpired,
  refreshSessionExpiration,
} from '/src/utils';

const CHECK_INTERVAL_MS = 30000;
const AUTH_PATHS = ['/login', '/logout', '/reset-password', '/verify-email'];
const isAuthPath = (path: string) => AUTH_PATHS.some((p) => path === p || path.startsWith(`${p}/`));

export const SessionManager: React.FC = observer(() => {
  const userStore = useUserStore();
  const globalStore = useGlobalStore();
  const navigate = useNavigate();

  useEffect(() => {
    const expireSession = () => {
      if (!userStore.isLoggedIn || isAuthPath(window.location.pathname)) return;
      globalStore.setPreviousPath(`${window.location.pathname}${window.location.search}${window.location.hash}`);
      navigate('/logout', { state: { reason: SESSION_EXPIRED_REASON } });
    };

    setUnauthorizedHandler(expireSession);
    setActivityHandler(() => {
      if (userStore.isLoggedIn) refreshSessionExpiration();
    });

    const check = () => {
      if (!userStore.isLoggedIn) return;
      if (!getSessionToken()) {
        expireSession();
        return;
      }
      if (getSessionExpiration() === undefined) refreshSessionExpiration();
      else if (isSessionExpired()) expireSession();
    };
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') check();
    };

    check();
    const interval = setInterval(check, CHECK_INTERVAL_MS);
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('focus', check);

    return () => {
      setUnauthorizedHandler(undefined);
      setActivityHandler(undefined);
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('focus', check);
    };
  }, []);

  return null;
});
